/**
 * JSON-LD schema components.
 *
 * These were POC stubs that all returned null, while still being rendered by
 * UnifiedFAQSection, ResourcesClient and TestimonialsSection -- so pages that looked like
 * they emitted structured data emitted none. The ones with real call sites now work.
 *
 * CONSTRAINT: this module is imported by client components (UnifiedFAQSection.tsx,
 * ResourcesClient.tsx -- both "use client") AND a server component
 * (features/template/TestimonialsSection.tsx). Keep every export synchronous and free of
 * server-only imports (next/headers, fs, DB clients), or the client graphs fail to compile.
 * A plain <script type="application/ld+json"> is safe in both and still server-renders on
 * first paint.
 *
 * Prop signatures are fixed by the existing call sites and must not tighten -- notably
 * SoftwareApplicationSchema is invoked with NO props at all.
 */
import { JsonLd } from "@/components/seo/JsonLd";
import {
  SITE_URL,
  faqPageNode,
  graph,
  normalizeFaqs,
  orgRef,
} from "@/lib/jsonLd";

type AnyProps = Record<string, unknown>;

/**
 * FAQPage. Note the prop is `questions` (not `faqs`) -- that is the existing contract at
 * UnifiedFAQSection.tsx:53 and ResourcesClient.tsx:97.
 *
 * Only render this where the same Q&A is visible on the page; marking up FAQs the user
 * cannot see is cloaking.
 */
export function FAQSchema({ questions }: { questions?: unknown } & AnyProps) {
  const node = faqPageNode(normalizeFaqs(questions));
  if (!node) return null;
  return <JsonLd data={graph(node)} />;
}

/**
 * The Hirebee app itself. Called with zero props, so everything is hard-coded here and every
 * prop stays optional. `publisher`/`provider` reference the canonical Organization by @id
 * rather than inlining another anonymous copy.
 */
export function SoftwareApplicationSchema(props: AnyProps = {}) {
  const name = (props.name as string) || "Hirebee";
  return (
    <JsonLd
      data={graph({
        "@type": "SoftwareApplication",
        name,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web-based",
        url: (props.url as string) || `${SITE_URL}/`,
        description:
          (props.description as string) ||
          "AI-powered applicant tracking and HR management platform for recruiting, candidate assessment, and hiring workflow automation.",
        publisher: orgRef,
        provider: orgRef,
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
          description: "Free 30-day trial",
        },
      })}
    />
  );
}

/**
 * Deliberately renders nothing, permanently.
 *
 * The only call site (features/template/TestimonialsSection.tsx:31) feeds this hand-authored
 * marketing quotes: the Testimonial type has no rating field at all, so `reviewRating: 5` is
 * a hard-coded literal and `datePublished` is the build date. Emitting Review/AggregateRating
 * built from fabricated ratings and dates violates Google's structured-data policy on
 * self-serving and fake reviews, and risks a manual action -- a real cost for no gain.
 *
 * The legitimate route to star ratings in search and AI answers is the third-party G2 /
 * Capterra / TrustRadius / GetApp profiles, which are claimed via Organization.sameAs in
 * src/lib/jsonLd.ts. Keep the export so the call site compiles unchanged.
 */
export function ReviewSchema(_props: AnyProps) {
  return null;
}

/*
 * The five below have NO call sites anywhere in src/. Organization and WebSite are already
 * emitted once site-wide from the root layout (src/lib/jsonLd.ts), and breadcrumb/article/
 * product schema is currently hand-rolled per page. Kept as no-ops rather than written
 * speculatively; give them a body when something actually needs them.
 */
export function OrganizationSchema(_props: AnyProps) {
  return null;
}
export function WebsiteSchema(_props: AnyProps) {
  return null;
}
export function BreadcrumbSchema(_props: AnyProps) {
  return null;
}
export function ArticleSchema(_props: AnyProps) {
  return null;
}
export function ProductSchema(_props: AnyProps) {
  return null;
}
